/**
 * MyProfile — /admin/settings/my-profile
 * Admin's own profile page.
 * Matches the EmployeeProfilePage design; adds global edit mode
 * (single "Edit Profile" button flips the page) with photo upload.
 *
 * Sections editable: Personal Info · Work Info · Emergency Contact · Bio & Skills
 * Read-only blocks retained: Stats, Badges, Activity, Streak weather
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Pencil, Mail, Bookmark, Sparkles, MessageCircle,
  BarChart3, Calendar, Award, Briefcase, Activity, Tag,
  ShieldCheck, Phone, MapPin, Clock, Zap, Trophy, Flame,
  CheckCircle, ListChecks, Sun, Moon, Star, Lock, Camera,
  Save, X, Settings, RefreshCw, Cake,
} from 'lucide-react';
import { API_URL } from '../../api/api';
import { todayStr } from '../../utils/localDate';
import { mergeSkillNames, skillNames } from '../../utils/skills';
import '../Team/profile.css';

import { toast } from "../../utils/toast";
// ─── Helpers (mirrors EmployeeProfilePage) ──────────────────────────────────

const auth = () => {
  const t = localStorage.getItem('token');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const resolvePhoto = (p) => {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  if (p.startsWith('data:')) return p;
  return `${API_URL}${p.startsWith('/') ? '' : '/'}${p}`;
};

const initialsOf = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('') || '?';

const THEMES = ['coral', 'cobalt', 'rose', 'emerald', 'plum'];
const themeFor = (id) => THEMES[(Number(id) || 0) % THEMES.length];

const tenureFromJoin = (dateStr) => {
  if (!dateStr) return { short: '—', months: 0 };
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return { short: '—', months: 0 };
  const now = new Date();
  const months = Math.max(0, (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()));
  const short = months >= 12 ? `${(months / 12).toFixed(1)}y` : months >= 1 ? `${months}mo` : `${Math.max(1, Math.round((now - d) / 86400000 / 7))}w`;
  return { short, months };
};

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const CountUp = ({ to, suffix = '', durationMs = 700 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const target = Number(to) || 0;
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - start) / durationMs);
      setVal(target * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(step); else setVal(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, durationMs]);
  const display = Number.isInteger(Number(to)) ? Math.round(val) : val.toFixed(1);
  return <span>{display}{suffix}</span>;
};

const XpRing = ({ level, current, total, size = 64 }) => {
  const pct = Math.max(0, Math.min(100, (current / Math.max(1, total)) * 100));
  return (
    <div className="xp-ring" style={{ '--ring-size': `${size}px`, '--ring-pct': `${pct * 3.6}deg` }} aria-label={`Level ${level}`}>
      <div className="xp-ring-fill" />
      <div className="xp-ring-inner">
        <span className="xp-ring-label">LV</span>
        <b className="xp-ring-num">{level}</b>
      </div>
    </div>
  );
};

const StreakWeather = ({ days = 30 }) => {
  const tiles = useMemo(() => {
    const out = []; const seed = days * 7 + 3;
    for (let i = 0; i < days; i++) {
      const v = (Math.sin(seed + i * 1.7) + 1) / 2;
      if (i % 7 === 6) out.push('off');
      else if (v > 0.92) out.push('absent');
      else if (v > 0.78) out.push('leave');
      else if (v > 0.58) out.push('late');
      else out.push('ontime');
    }
    return out;
  }, [days]);
  return (
    <div className="streak-weather">
      <div className="streak-weather-head">
        <span className="sw-title"><Calendar size={12} /> Last {days} days</span>
        <small>{tiles.filter((t) => t === 'ontime').length}/{days} on time</small>
      </div>
      <div className="streak-weather-tiles">
        {tiles.map((t, i) => <span key={i} className={`sw-tile sw-${t}`} />)}
      </div>
      <div className="streak-weather-legend">
        <span><i className="dot dot-absent" /> Absent</span>
        <span><i className="dot dot-late" /> Late</span>
        <span><i className="dot dot-ontime" /> On time</span>
      </div>
    </div>
  );
};

const StatChip = ({ icon: Icon, value, suffix, label }) => (
  <div className="stat-chip">
    <span className="stat-chip-icon"><Icon size={14} /></span>
    <b className="stat-chip-value"><CountUp to={value} suffix={suffix || ''} /></b>
    <span className="stat-chip-label">{label}</span>
  </div>
);

const StatTile = ({ icon: Icon, value, suffix, label, sub }) => (
  <div className="stat-tile">
    <span className="stat-tile-icon"><Icon size={14} /></span>
    <b className="stat-tile-value"><CountUp to={value} suffix={suffix || ''} /></b>
    <span className="stat-tile-label">{label}</span>
    {sub && <small className="stat-tile-sub">{sub}</small>}
  </div>
);

const BadgeTile = ({ icon: Icon, label, sub, locked, gold }) => (
  <div className={`badge-tile ${locked ? 'is-locked' : ''} ${gold ? 'is-gold' : ''}`}>
    {gold && <span className="badge-ribbon">GOLD</span>}
    <span className="badge-orb">{locked ? <Lock size={14} /> : <Icon size={16} />}</span>
    <b className="badge-label">{label}</b>
    <small className="badge-sub">{sub}</small>
  </div>
);

// ─── Form field primitives ────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', border: '1px solid var(--em-border)', borderRadius: 6,
  padding: '8px 10px', fontSize: 13, outline: 'none',
  boxSizing: 'border-box', background: 'var(--em-bg-card)', color: '#111827',
  transition: 'border-color 0.15s',
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--em-fg5)', marginBottom: 4,
  textTransform: 'uppercase', letterSpacing: '0.5px',
};

const Field = ({ label, value, onChange, type = 'text', multiline, rows = 3, placeholder, hint, readOnly, max }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {multiline ? (
      <textarea value={value} onChange={onChange} rows={rows} placeholder={placeholder} readOnly={readOnly}
        style={{ ...inputStyle, resize: 'vertical' }} />
    ) : (
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly} max={max}
        style={{ ...inputStyle, ...(readOnly ? { background: 'var(--em-bg-head)', color: '#9ca3af', cursor: 'not-allowed' } : {}) }} />
    )}
    {hint && <small style={{ fontSize: 10, color: '#9ca3af', marginTop: 2, display: 'block' }}>{hint}</small>}
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder = '— Select —' }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    <select value={value} onChange={onChange}
      style={{ ...inputStyle, appearance: 'auto' }}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// ─── Edit tab config ──────────────────────────────────────────────────────────

const EDIT_TABS = [
  { key: 'personal', label: 'Personal' },
  { key: 'work', label: 'Work' },
  { key: 'emergency', label: 'Emergency' },
  { key: 'bio', label: 'Bio' },
];

// ─── Main component ──────────────────────────────────────────────────────────

export default function MyProfile() {
  const navigate = useNavigate();

  // The sidebar exposes My Profile to admin and hr — keep in-page links on the
  // caller's own settings branch instead of hardcoding /admin.
  const settingsBase = useMemo(() => {
    const role = (localStorage.getItem('role') || '').toLowerCase().trim();
    if (role === 'admin') return '/admin/settings';
    if (role === 'hr') return '/hr/settings';
    return '/member/settings';
  }, []);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [theme, setTheme] = useState('light');
  const [bookmarked, setBookmarked] = useState(false);

  const photoInputRef = useRef(null);

  // Theme persistence
  useEffect(() => {
    const saved = localStorage.getItem('emhub_profile_theme');
    if (saved === 'dark' || saved === 'light') setTheme(saved);
  }, []);
  useEffect(() => { localStorage.setItem('emhub_profile_theme', theme); }, [theme]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    // The profile lives in team_members. /api/users/me resolves it from the
    // JWT (users.member_id, with an email fallback) — don't pass an id here:
    // /api/users/:id matches on users.id, so sending member_id 404s.
    axios.get(`${API_URL}/api/users/me`, auth())
      .then((r) => setProfile(r.data))
      .catch((err) => setError(err?.response?.data?.error || 'Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const fullName = useMemo(() => {
    if (!profile) return '';
    return `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username || 'Admin';
  }, [profile]);

  const designation = profile?.jobtitle || profile?.designation || 'Administrator';
  const department = profile?.department || '';
  const tenure = useMemo(() => tenureFromJoin(profile?.join_date || profile?.doj), [profile]);
  const handle = (profile?.email || profile?.user_email || '').split('@')[0];

  // The column holds the structured list built in the team wizard (or, on
  // older rows, a plain comma-separated string) — the tags need only names.
  const skills = useMemo(() => skillNames(profile?.skills), [profile?.skills]);

  const themeKey = useMemo(() => themeFor(profile?.id || 1), [profile?.id]);

  const fakeStats = useMemo(() => {
    if (!profile) return null;
    const seed = (profile.id || 1) * 17 + 3;
    const r = (n) => Math.abs(Math.sin(seed * n)) % 1;
    return {
      level: 1 + Math.floor(r(2) * 14),
      xpCurrent: Math.floor(r(3) * 7000) + 500,
      xpTotal: 8000,
      dayStreak: Math.floor(r(4) * 50),
      onTime: 80 + Math.floor(r(5) * 20),
      tasks: Math.floor(r(6) * 60),
      teamRank: 1 + Math.floor(r(7) * 5),
      teamSize: 14,
      hoursMonth: 100 + Math.floor(r(8) * 80),
      leavesUsed: Math.floor(r(9) * 15),
      leavesTotal: 22,
    };
  }, [profile]);

  const currentPhotoUrl = photoPreview || resolvePhoto(profile?.profile_photo);

  // ── Edit mode ──────────────────────────────────────────────────────────────
  const enterEdit = () => {
    setForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      contact_number: profile.contact_number || '',
      dob: profile.dob ? profile.dob.split('T')[0] : '',
      gender: profile.gender || '',
      nationality: profile.nationality || '',
      address: profile.address || '',
      blood_group: profile.blood_group || '',
      jobtitle: profile.jobtitle || '',
      designation: profile.designation || '',
      department: profile.department || '',
      work_location: profile.work_location || '',
      work_shift: profile.work_shift || '',
      additional_notes: profile.additional_notes || '',
      // Edited here as a plain comma-separated list; the proficiency, program
      // and certificate each skill carries are merged back on save.
      skills: skillNames(profile.skills).join(', '),
      emergency_contact_name: profile.emergency_contact_name || '',
      emergency_contact_relation: profile.emergency_contact_relation || '',
      emergency_contact_phone: profile.emergency_contact_phone || '',
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setActiveTab('personal');
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const saveProfile = async () => {
    if (!profile?.id) { toast.error('No team member record found for this account.'); return; }
    // The input's `max` only guides the picker — check the value before we
    // send it so the user gets told here rather than by a server rejection.
    if (form.dob && form.dob > todayStr()) {
      toast.error("Date of birth can't be in the future.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        // The names typed here are folded back into the stored list so a skill
        // that keeps its name keeps its proficiency and certificate too.
        if (k === 'skills') return fd.append(k, mergeSkillNames(profile.skills, v));
        fd.append(k, v);
      });
      if (photoFile) fd.append('profile_photo', photoFile);

      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/api/team/${profile.id}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Merge updated team_member data back into profile (preserving user fields)
      setProfile((prev) => ({
        ...prev,
        ...res.data,
        // Keep preview as local photo until page refresh
        ...(photoPreview ? { profile_photo: photoPreview } : {}),
      }));

      // Live-sync the header immediately (no page refresh needed)
      window.dispatchEvent(new CustomEvent('profileUpdated', {
        detail: {
          first_name: form.first_name,
          last_name: form.last_name,
          jobtitle: form.jobtitle,
          profile_photo: photoPreview || resolvePhoto(res.data?.profile_photo || profile.profile_photo),
          role: profile.role,
        },
      }));

      setEditMode(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSaveMsg('Profile updated successfully!');
      setTimeout(() => setSaveMsg(''), 3500);
    } catch (err) {
      setSaveMsg('❌ ' + (err?.response?.data?.error || 'Save failed. Please try again.'));
      setTimeout(() => setSaveMsg(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) return (
    <div className="profile-page">
      <p style={{ padding: 40, color: 'var(--em-fg5)' }}>Loading profile…</p>
    </div>
  );
  if (error || !profile) return (
    <div className="profile-page">
      <p style={{ padding: 40, color: '#dc2626' }}>⚠ {error || 'Profile not found'}</p>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={`profile-page profile-theme-${themeKey} profile-mode-${theme}`}>

      {/* Theme toggle */}
      <button type="button" className="profile-theme-toggle"
        onClick={() => setTheme((t) => t === 'dark' ? 'light' : 'dark')}
        aria-label="Toggle theme" title="Toggle theme">
        {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
      </button>

      {/* Save notification toast */}
      {saveMsg && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: saveMsg.startsWith('❌') ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${saveMsg.startsWith('❌') ? '#fca5a5' : '#86efac'}`,
          color: saveMsg.startsWith('❌') ? '#dc2626' : '#15803d',
          borderRadius: 10, padding: '12px 20px', fontSize: 13,
          fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          {saveMsg}
        </div>
      )}

      {/* ═══════════════ HERO BLOCK ═══════════════ */}
      <section className="profile-hero">

        {/* LEFT — Photo card */}
        <div className="hero-photo-card">
          <div className="hero-photo-bg" style={{
            backgroundImage: currentPhotoUrl ? `url(${currentPhotoUrl})` : undefined,
          }}>
            {!currentPhotoUrl && (
              <span className="hero-photo-initials">{initialsOf(fullName)}</span>
            )}
          </div>
          <span className="hero-grain" aria-hidden="true" />

          {/* Photo upload overlay — edit mode only */}
          {editMode && (
            <div
              onClick={() => photoInputRef.current?.click()}
              style={{
                position: 'absolute', inset: 0, zIndex: 10,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', borderRadius: 'inherit',
                transition: 'background 0.2s',
              }}
            >
              <Camera size={28} color="#fff" />
              <span style={{ fontSize: 12, color: '#fff', marginTop: 6, fontWeight: 600 }}>
                {photoPreview ? 'Change photo' : 'Upload photo'}
              </span>
            </div>
          )}
          <input
            ref={photoInputRef} type="file" accept="image/*"
            style={{ display: 'none' }} onChange={handlePhotoChange}
          />

          {/* Top row — back + actions */}
          <div className="hero-photo-top">
            <button
              className="glass-pill icon-only"
              onClick={() => navigate(settingsBase)}
              title="Back to settings" aria-label="Back to settings"
            >
              <ArrowLeft size={14} />
            </button>
            <div className="hero-photo-actions">
              {editMode ? (
                <>
                  <button
                    className="glass-pill primary"
                    onClick={saveProfile} disabled={saving}
                  >
                    {saving
                      ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                      : <><Save size={12} /> Save</>
                    }
                  </button>
                  <button className="glass-pill icon-only" onClick={cancelEdit} title="Cancel">
                    <X size={14} />
                  </button>
                </>
              ) : (
                <button className="glass-pill primary" onClick={enterEdit}>
                  <Pencil size={12} /> Edit Profile
                </button>
              )}
            </div>
          </div>

          {/* Name + role */}
          <div className="hero-photo-name">
            <h1>{fullName || 'Admin'}</h1>
            <span className="hero-role-pill">
              <span className="dot-spin" /> {designation}
            </span>
          </div>

          {/* Now strip */}
          <div className="hero-now-strip">
            <span className="np-bars" aria-hidden="true"><i /><i /><i /><i /></span>
            <span className="np-text">
              <b>Available</b>
              <small>Open for collabs</small>
            </span>
          </div>

          {/* Handle row */}
          <div className="hero-handle-row">
            <div className="hero-handle">
              <span className="hh-avatar">{initialsOf(fullName)}</span>
              <div className="hh-text">
                <b>@{handle || 'admin'}</b>
                <small>Active now</small>
              </div>
            </div>
            <a
              className="hero-message-btn"
              href={`mailto:${profile.email || profile.user_email}`}
              title="Send email"
            >
              <MessageCircle size={12} /> Message
            </a>
          </div>
        </div>

        {/* RIGHT — Info card */}
        <div className="hero-info-card">
          <div className="hero-info-top">
            <div className="hero-info-tags">
              {department && (
                <span className="info-dept-pill">{String(department).toUpperCase()}</span>
              )}
              <span className="info-status-pill">
                <span className="dot-active" /> Active
              </span>
            </div>
            <span className="info-id">ID · EM-{String(profile.id || 0).padStart(4, '0')}</span>
          </div>

          {/* Bio — editable in edit mode */}
          {editMode ? (
            <textarea
              value={form.additional_notes}
              onChange={set('additional_notes')}
              placeholder="Write a short bio about yourself…"
              rows={3}
              style={{
                width: '100%', border: '1px solid rgba(255,255,255,0.25)',
                background: 'rgba(255,255,255,0.12)', borderRadius: 8,
                padding: '8px 10px', fontSize: 13, color: 'inherit',
                resize: 'none', outline: 'none', marginBottom: 12,
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <p className="hero-bio">
              {profile.additional_notes || `${designation} at ElectronikMedia.`}
            </p>
          )}

          {/* XP block */}
          <div className="xp-block">
            <XpRing level={fakeStats.level} current={fakeStats.xpCurrent} total={fakeStats.xpTotal} size={68} />
            <div className="xp-meta">
              <div className="xp-line">
                <b>Level {fakeStats.level} · {fakeStats.xpCurrent.toLocaleString()} / {fakeStats.xpTotal.toLocaleString()} XP</b>
                <small>{Math.max(0, fakeStats.xpTotal - fakeStats.xpCurrent)} to L{fakeStats.level + 1}</small>
              </div>
              <div className="xp-bar">
                <span className="xp-fill" style={{ width: `${(fakeStats.xpCurrent / fakeStats.xpTotal) * 100}%` }} />
              </div>
              <small className="xp-hint">Next badge unlocks at L{fakeStats.level + 1}</small>
            </div>
          </div>

          {/* Stat chips */}
          <div className="stat-chip-row">
            <StatChip icon={Flame} value={fakeStats.dayStreak} label="Day streak" />
            <StatChip icon={Clock} value={fakeStats.onTime} suffix="%" label="On-time" />
            <StatChip icon={CheckCircle} value={fakeStats.tasks} label="Tasks" />
            <StatChip icon={Trophy} value={`#${fakeStats.teamRank}`} label={`of ${fakeStats.teamSize}`} />
          </div>

          <StreakWeather days={30} />

          {/* Action row */}
          <div className="hero-action-row">
            <a href={`mailto:${profile.email || profile.user_email}`} className="action-secondary">
              <Mail size={14} /> Email
            </a>
            <button
              className={`action-bookmark ${bookmarked ? 'is-on' : ''}`}
              onClick={() => setBookmarked((b) => !b)}
              aria-label="Bookmark"
            >
              <Bookmark size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════ TWO-COLUMN GRID ═══════════════ */}
      <section className="profile-grid">

        {/* ── MAIN COLUMN ─────────────────────────────── */}
        <div className="profile-main">

          {/* Stats card */}
          <div className="profile-card">
            <h3 className="profile-card-head">
              <span className="profile-card-icon"><BarChart3 size={14} /></span>
              Stats
            </h3>
            <div className="stat-tile-grid">
              <StatTile icon={Clock} value={fakeStats.hoursMonth} label="Hours this month" />
              <StatTile icon={Calendar} value={`${fakeStats.leavesUsed}/${fakeStats.leavesTotal}`} label="Leaves used" sub={`${fakeStats.leavesTotal - fakeStats.leavesUsed} remaining`} />
              <StatTile icon={Flame} value={fakeStats.dayStreak} label="Day streak" sub={`best: ${fakeStats.dayStreak + 9}`} />
              <StatTile icon={CheckCircle} value={fakeStats.onTime} suffix="%" label="On-time rate" sub="30-day rolling" />
              <StatTile icon={Briefcase} value={tenure.short} label="Tenure" sub={`${tenure.months} months`} />
              <StatTile icon={ListChecks} value={fakeStats.tasks} label="Tasks done" sub="this quarter" />
              <StatTile icon={Trophy} value={`#${fakeStats.teamRank}`} label="Team rank" sub={`of ${fakeStats.teamSize}`} />
              <StatTile icon={Star} value={fakeStats.level} label="Level" sub={`${fakeStats.xpCurrent}/${fakeStats.xpTotal} XP`} />
            </div>
          </div>

          {/* Badges */}
          <div className="profile-card">
            <h3 className="profile-card-head">
              <span className="profile-card-icon"><Award size={14} /></span>
              Badges &amp; achievements
              <small className="profile-card-meta">6 earned · 2 locked</small>
            </h3>
            <div className="badge-grid">
              <BadgeTile icon={Flame} label="30-Day Streak" sub="2d ago" gold />
              <BadgeTile icon={CheckCircle} label="Perfect Month" sub="1w ago" />
              <BadgeTile icon={Cake} label="2 Years" sub="4mo ago" />
              <BadgeTile icon={Zap} label="Launch Pro" sub="×5 · 3w ago" gold />
              <BadgeTile icon={Activity} label="No-Overtime Week" sub="2w ago" />
              <BadgeTile icon={Award} label="Course Champion" sub="×3 · 1mo ago" />
              <BadgeTile icon={Lock} label="5 Years" sub="Locked" locked />
              <BadgeTile icon={Lock} label="100-Day Streak" sub="38/100" locked />
            </div>
          </div>

          {/* Recent activity */}
          <div className="profile-card">
            <h3 className="profile-card-head">
              <span className="profile-card-icon"><Activity size={14} /></span>
              Recent activity
            </h3>
            <ul className="activity-list">
              {[
                { icon: Sparkles, tone: 'kudos', text: 'Received kudos from the team', when: '2h ago' },
                { icon: Clock, tone: 'time', text: 'Clocked in at 09:02 · on time', when: '7h ago' },
                { icon: CheckCircle, tone: 'task', text: 'Completed: "Q2 review" · +120 XP', when: 'Yesterday' },
                { icon: Flame, tone: 'badge', text: 'Earned "30-Day Streak"', when: '2d ago' },
                { icon: Calendar, tone: 'leave', text: `Took 1 day off · ${fakeStats.leavesUsed}/${fakeStats.leavesTotal} used`, when: 'Last week' },
                { icon: Zap, tone: 'ship', text: 'Launched: Newsletter module · +500 XP', when: '3w ago' },
              ].map((a, i) => (
                <li key={i} className={`activity-row tone-${a.tone}`}>
                  <span className="activity-icon"><a.icon size={14} /></span>
                  <div className="activity-body">
                    <p className="activity-text">{a.text}</p>
                    <small className="activity-when">{a.when}</small>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── SIDEBAR ──────────────────────────────────── */}
        <aside className="profile-side">

          {editMode ? (
            /* ── EDIT FORM ── */
            <div className="profile-card">
              <h3 className="profile-card-head">
                <span className="profile-card-icon"><Pencil size={14} /></span>
                Edit Profile
              </h3>

              {/* Tab strip */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                {EDIT_TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    style={{
                      flex: 1, padding: '7px 0',
                      border: 'none', borderRadius: 7, cursor: 'pointer',
                      fontSize: 11, fontWeight: 600,
                      background: activeTab === key ? '#f54b01' : '#f3f4f6',
                      color: activeTab === key ? '#fff' : '#374151',
                      transition: 'all 0.15s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* ── Personal tab ── */}
              {activeTab === 'personal' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="First Name" value={form.first_name} onChange={set('first_name')} />
                    <Field label="Last Name" value={form.last_name} onChange={set('last_name')} />
                  </div>
                  <Field label="Contact Number" value={form.contact_number} onChange={set('contact_number')} type="tel" />
                  <Field label="Date of Birth" value={form.dob} onChange={set('dob')} type="date" max={todayStr()} />
                  <SelectField label="Gender" value={form.gender} onChange={set('gender')}
                    options={['Male', 'Female', 'Other', 'Prefer not to say']} />
                  <Field label="Nationality" value={form.nationality} onChange={set('nationality')} />
                  <Field label="Address" value={form.address} onChange={set('address')} multiline rows={2} />
                  <SelectField label="Blood Group" value={form.blood_group} onChange={set('blood_group')}
                    options={['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']} />
                </div>
              )}

              {/* ── Work tab ── */}
              {activeTab === 'work' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Field label="Job Title" value={form.jobtitle} onChange={set('jobtitle')} />
                  <Field label="Designation" value={form.designation} onChange={set('designation')} />
                  <Field label="Department" value={form.department} onChange={set('department')} />
                  <Field label="Work Location" value={form.work_location} onChange={set('work_location')} />
                  <Field label="Work Shift" value={form.work_shift} onChange={set('work_shift')} />
                  <Field label="Date of Joining" value={profile.doj ? fmtDate(profile.doj) : '—'} readOnly hint="Contact super admin to change" />
                </div>
              )}

              {/* ── Emergency tab ── */}
              {activeTab === 'emergency' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Field label="Contact Name" value={form.emergency_contact_name} onChange={set('emergency_contact_name')} />
                  <Field label="Relationship" value={form.emergency_contact_relation} onChange={set('emergency_contact_relation')} />
                  <Field label="Phone Number" value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} type="tel" />
                </div>
              )}

              {/* ── Bio tab ── */}
              {activeTab === 'bio' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Field label="Bio / About" value={form.additional_notes} onChange={set('additional_notes')}
                    multiline rows={5} placeholder="Write a short bio about yourself…" />
                  <Field label="Skills" value={form.skills} onChange={set('skills')}
                    placeholder="React, Node.js, Leadership…"
                    hint="Comma-separated — these appear as tag chips on your profile" />
                </div>
              )}

              {/* Save / Cancel row */}
              <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
                <button
                  onClick={saveProfile} disabled={saving}
                  style={{
                    flex: 2, padding: '11px 0', background: '#f54b01', color: '#fff',
                    border: 'none', borderRadius: 8, fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13,
                    opacity: saving ? 0.8 : 1, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6,
                  }}
                >
                  {saving ? <><RefreshCw size={13} /> Saving…</> : <><Save size={13} /> Save changes</>}
                </button>
                <button
                  onClick={cancelEdit}
                  style={{
                    flex: 1, padding: '11px 0', background: 'var(--em-bg-muted)', color: '#374151',
                    border: 'none', borderRadius: 8, fontWeight: 600,
                    cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>

          ) : (
            /* ── READ MODE ── */
            <>
              {/* Personal & Work Info */}
              <div className="profile-card">
                <h3 className="profile-card-head">
                  <span className="profile-card-icon"><ShieldCheck size={14} /></span>
                  Personal &amp; work info
                </h3>
                <dl className="info-pairs">
                  {[
                    { dt: 'Employee ID', dd: `EM-${String(profile.id || 0).padStart(4, '0')}` },
                    { dt: 'Job Title', dd: profile.jobtitle || '—' },
                    { dt: 'Designation', dd: profile.designation || '—' },
                    { dt: 'Department', dd: department || '—' },
                    { dt: 'Work Shift', dd: profile.work_shift || '—' },
                    { dt: 'Date of Joining', dd: fmtDate(profile.join_date || profile.doj) },
                    { dt: 'Tenure', dd: tenure.short },
                    { dt: 'Work Location', dd: profile.work_location || '—' },
                    { dt: 'Date of Birth', dd: fmtDate(profile.dob) },
                    { dt: 'Gender', dd: profile.gender || '—' },
                    { dt: 'Nationality', dd: profile.nationality || '—' },
                    { dt: 'Blood Group', dd: profile.blood_group || '—' },
                  ].map(({ dt, dd }) => (
                    <div key={dt}>
                      <dt>{dt}</dt>
                      <dd>{dd}</dd>
                    </div>
                  ))}
                </dl>

                {/* Contact rows */}
                <div className="info-row">
                  <dt><Mail size={12} /> Email</dt>
                  <dd><a href={`mailto:${profile.email || profile.user_email}`}>{profile.email || profile.user_email}</a></dd>
                </div>
                {profile.contact_number && (
                  <div className="info-row">
                    <dt><Phone size={12} /> Phone</dt>
                    <dd>{profile.contact_number}</dd>
                  </div>
                )}
                {profile.address && (
                  <div className="info-row">
                    <dt><MapPin size={12} /> Address</dt>
                    <dd>{profile.address}</dd>
                  </div>
                )}

                {/* Emergency contact */}
                {(profile.emergency_contact_name || profile.emergency_contact_phone) && (
                  <div className="emergency-block">
                    <h4><Phone size={12} /> Emergency contact</h4>
                    <p>
                      <b>{profile.emergency_contact_name || '—'}</b>
                      {profile.emergency_contact_relation ? ` · ${profile.emergency_contact_relation}` : ''}
                    </p>
                    {profile.emergency_contact_phone && <p><small>{profile.emergency_contact_phone}</small></p>}
                  </div>
                )}
              </div>

              {/* Skills & Bio */}
              <div className="profile-card">
                <h3 className="profile-card-head">
                  <span className="profile-card-icon"><Tag size={14} /></span>
                  Skills &amp; bio
                </h3>

                {profile.additional_notes && (
                  <p style={{ fontSize: 13, color: 'var(--pc-muted, #6b7280)', lineHeight: 1.6, marginBottom: skills.length ? 14 : 0 }}>
                    {profile.additional_notes}
                  </p>
                )}

                {skills.length === 0 && !profile.additional_notes ? (
                  <p className="muted">No bio or skills recorded yet.</p>
                ) : skills.length > 0 ? (
                  <div className="tag-cloud">
                    {skills.map((s) => (
                      <span key={s} className="tag-pill">
                        @{s.replace(/^@/, '').replace(/\s+/g, '-').toLowerCase()}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Quick links — dark admin card */}
              <div className="profile-card admin-card">
                <h3 className="profile-card-head light">
                  <span className="profile-card-icon ghost"><Settings size={14} /></span>
                  Quick links
                </h3>
                <small className="admin-sub">Admin account shortcuts</small>
                <Link to={`${settingsBase}/password-management`} className="admin-btn">
                  <Lock size={14} /> Change password <span className="chev">›</span>
                </Link>
                <Link to={`${settingsBase}/company-info`} className="admin-btn">
                  <Briefcase size={14} /> Company settings <span className="chev">›</span>
                </Link>
                <button className="admin-btn" onClick={enterEdit}>
                  <Pencil size={14} /> Edit profile <span className="chev">›</span>
                </button>
              </div>
            </>
          )}
        </aside>
      </section>
    </div>
  );
}
